import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'
import { InteractionRequiredAuthError } from '@azure/msal-browser'

/** Profile data surfaced in the UI (top-right menu and chat avatars). */
export interface GraphUserProfile {
  firstName: string
  displayName: string
  photoUrl: string | null
}

const GRAPH_ROOT = 'https://graph.microsoft.com/v1.0'
const GRAPH_SCOPES = ['User.Read']

/** Best-effort first name from a display name or UPN (e.g. "Eduard Pleșu" -> "Eduard"). */
export function deriveFirstName(name?: string, username?: string): string {
  const trimmed = name?.trim()
  if (trimmed) return trimmed.split(/\s+/)[0]
  const local = username?.split('@')[0] ?? ''
  const first = local.split(/[._-]/)[0]
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : ''
}

/**
 * Loads the signed-in user's Graph profile and photo. Purely additive/cosmetic:
 * any failure (no consent, no photo, offline) resolves to sensible fallbacks
 * derived from the MSAL account so the UI never breaks.
 */
export async function loadUserProfile(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<GraphUserProfile> {
  const fallback: GraphUserProfile = {
    firstName: deriveFirstName(account.name, account.username),
    displayName: account.name || account.username,
    photoUrl: null,
  }

  let token: string
  try {
    const result = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account })
    token = result.accessToken
  } catch (error) {
    // Never force interaction just to fetch a photo — degrade to initials.
    if (error instanceof InteractionRequiredAuthError) return fallback
    return fallback
  }

  const authHeader = { Authorization: `Bearer ${token}` }

  const [profile, photoUrl] = await Promise.all([
    fetchProfile(authHeader),
    fetchPhoto(authHeader),
  ])

  return {
    firstName: profile.firstName || fallback.firstName,
    displayName: profile.displayName || fallback.displayName,
    photoUrl,
  }
}

async function fetchProfile(
  authHeader: Record<string, string>,
): Promise<{ firstName: string; displayName: string }> {
  try {
    const res = await fetch(`${GRAPH_ROOT}/me?$select=givenName,displayName`, { headers: authHeader })
    if (!res.ok) return { firstName: '', displayName: '' }
    const data = (await res.json()) as { givenName?: string; displayName?: string }
    return { firstName: data.givenName ?? '', displayName: data.displayName ?? '' }
  } catch {
    return { firstName: '', displayName: '' }
  }
}

async function fetchPhoto(authHeader: Record<string, string>): Promise<string | null> {
  try {
    const res = await fetch(`${GRAPH_ROOT}/me/photo/$value`, { headers: authHeader })
    if (!res.ok) return null
    const blob = await res.blob()
    if (blob.size === 0) return null
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}
